import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import SecondaryMenu from '../../menu/secondaryMenu.component';
import FormikProvider from '../../shared/forms/formikProvider.component';
import Form from '../../shared/forms/form.component';

import { FormSchemaPropTypes, FormikPropTypes } from '../../shared/forms/propTypes';
import Button from '../../shared/button.component';
import AgGrid from '../../shared/agGrid/agGrid.component';

import schema from './databaseLogsMenu.schema';
import {
  fetchDatabaseLogHeaders,
  fetchDatabaseLogDetail,
} from './store/actions';
import {
  isLoadingDbLogHeadersSelector,
  isLoadingDbLogDetailSelector,
  dbLogHeadersSelector,
  dbLogDetailSelector,
} from './store/selectors';
import {
  headerColumnDefinitions,
  detailColumnDefinitions,
} from './columnDefinitions';

const renderForm = ({
  isLoadingDbLogHeaders,
  setNote,
}) => {
  const DatabaseLogsMenu = ({ formik, schema }) => {
    const { note } = formik.values;

    useEffect(() => {
      setNote(note);
    }, [note]);

    return (
      <>
        <Form
          formik={formik}
          schema={schema}
        />
        <Button
          onClick={formik.handleSubmit}
          isLoading={isLoadingDbLogHeaders}
        >
          Search
        </Button>
      </>
    );
  };

  DatabaseLogsMenu.propTypes = {
    formik: FormikPropTypes,
    schema: FormSchemaPropTypes,
  };

  return DatabaseLogsMenu;
};

const DatabaseLogsPage = ({
  fetchDatabaseLogHeaders,
  fetchDatabaseLogDetail,
  isLoadingDbLogDetail,
  isLoadingDbLogHeaders,
  headers,
  detail,
}) => {
  const [note, setNote] = useState('');
  const handleSubmit = useCallback(({ note, range }) => {
    if (!range || !range.to || !range.from) {
      return;
    }

    fetchDatabaseLogHeaders({
      note,
      ...range,
    });
  }, [fetchDatabaseLogHeaders]);

  const handleSelectionChanged = useCallback(({ api }) => {
    const selectedNodes = api.getSelectedNodes();

    if (!selectedNodes[0]) {
      return;
    }

    fetchDatabaseLogDetail({
      headerId: selectedNodes[0].data.HDR_ID,
      note,
    });
  }, [fetchDatabaseLogDetail, note]);

  return (
    <div className="database-logs-page">
      <SecondaryMenu>
        <FormikProvider
          schema={schema}
          render={renderForm({ isLoadingDbLogHeaders, setNote })}
          onSubmit={handleSubmit}
        />
      </SecondaryMenu>
      <div className="page">
        <div className="container-fluid">
          <div className="row no-gutters page-content-container">
            <div className="col">
              <h5>Database Logs</h5>
            </div>
          </div>
          <div className="row">
            <div className="col">
              <div className="ag-theme-bootstrap">
                <AgGrid
                  rowData={headers}
                  columnDefs={headerColumnDefinitions}
                  onSelectionChanged={handleSelectionChanged}
                  isGridLoading={isLoadingDbLogHeaders}
                />
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col">
              <div className="ag-theme-bootstrap">
                <AgGrid
                  rowData={detail}
                  columnDefs={detailColumnDefinitions}
                  isGridLoading={isLoadingDbLogDetail}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

DatabaseLogsPage.propTypes = {
  fetchDatabaseLogHeaders: PropTypes.func.isRequired,
  fetchDatabaseLogDetail: PropTypes.func.isRequired,
  isLoadingDbLogHeaders: PropTypes.bool.isRequired,
  isLoadingDbLogDetail: PropTypes.bool.isRequired,

  headers: PropTypes.arrayOf(PropTypes.shape({
    HDR_ID: PropTypes.number.isRequired,
    CODE: PropTypes.string,
    NOTE: PropTypes.string,
    PRG_UNIT: PropTypes.string,
    START_TS: PropTypes.string,
    END_TS: PropTypes.string,
    CREATED_BY: PropTypes.string,
    CREATED_ON: PropTypes.string,
    UPDATED_BY: PropTypes.string,
    UPDATED_ON: PropTypes.string,
  })),

  detail: PropTypes.arrayOf(PropTypes.shape({
    DTL_ID: PropTypes.number.isRequired,
    HDR_ID: PropTypes.number.isRequired,
    CODE: PropTypes.string,
    NOTE: PropTypes.string,
    PRG_UNIT: PropTypes.string,
    DATE_TS: PropTypes.string,
  })),
};

const mapStateToProps = state => ({
  isLoadingDbLogDetail: isLoadingDbLogDetailSelector(state),
  isLoadingDbLogHeaders: isLoadingDbLogHeadersSelector(state),
  headers: dbLogHeadersSelector(state),
  detail: dbLogDetailSelector(state),
});

export default connect(
  mapStateToProps, {
    fetchDatabaseLogHeaders,
    fetchDatabaseLogDetail,
  }
)(DatabaseLogsPage);
